/**
 * Tab Store - Zustand store for managing tabs per project.
 *
 * This store owns all tab state and logic. Persistence to the server
 * is handled separately via useUserPreferencesStore.
 */
import { create } from 'zustand';
import type { Tab, TabItem } from '../types';
import { extensionIsNotable, fileExtension } from '../utils/filename';
import { useConversationStreamStore } from './conversationStreamStore';

interface ProjectTabState {
  tabs: Tab[];
  activeTabId: string | null;
}

interface TabStore {
  // State: tabs per project
  tabsByProject: Record<string, ProjectTabState>;
  // True immediately after loadFromPreferences, cleared on first real mutation.
  // Used by TabContext to skip persisting unchanged data back to the server.
  _loadedFromPreferences: boolean;

  // Actions
  openTab: (projectId: string, item: TabItem) => string | undefined;
  closeTab: (projectId: string, tabId: string) => void;
  setActiveTab: (projectId: string, tabId: string) => void;
  clearActiveTab: (projectId: string) => void;
  updateTabTitle: (projectId: string, itemId: string, newTitle: string) => void;
  openDraftTab: (projectId: string, title?: string) => string;
  closeDraftTab: (projectId: string) => void;
  convertDraftToTab: (projectId: string, itemId: string, title?: string) => void;

  /** Move one strip's tabs under another key, keeping any already there. */
  rekey: (from: string, to: string) => void;

  // Persistence helpers
  loadFromPreferences: (tabsByProject: Record<string, { tabs: Tab[]; activeTabId?: string }> | undefined) => void;
  getProjectState: (projectId: string) => ProjectTabState;
}

const emptyProjectState: ProjectTabState = { tabs: [], activeTabId: null };

/** How many items one set keeps open. Nothing on screen closes one — the
 *  rail lists everything that can be opened — so the set is bounded here. */
const MAX_OPEN = 12;

/**
 * Make room for one more: drop the longest-open items past the cap. A draft
 * stays, and so does a conversation still streaming — its reply is being
 * written into the state that dropping it would clear.
 */
function withRoom(tabs: Tab[]): Tab[] {
  let excess = tabs.length - (MAX_OPEN - 1);
  if (excess <= 0) return tabs;
  const streams = useConversationStreamStore.getState();
  return tabs.filter((tab) => {
    if (excess <= 0 || tab.isDraft) return true;
    const key = tab.storeKey || tab.itemId;
    if (tab.itemType === 'conversation') {
      if (streams.conversations.get(key)?.isLoading) return true;
      streams.cleanupConversation(key);
    }
    excess -= 1;
    return false;
  });
}

export const useTabStore = create<TabStore>((set, get) => ({
  tabsByProject: {},
  _loadedFromPreferences: false,

  getProjectState: (projectId: string) => {
    return get().tabsByProject[projectId] || emptyProjectState;
  },

  openTab: (projectId: string, item: TabItem) => {
    const tabId = `tab-${item.id}`;
    let returnStoreKey: string | undefined;

    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      const existingTab = projectState.tabs.find((t) => t.itemId === item.id);

      if (existingTab) {
        returnStoreKey = existingTab.storeKey;
        return {
          tabsByProject: {
            ...state.tabsByProject,
            [projectId]: {
              tabs: projectState.tabs.map((t) => ({ ...t, isActive: t.id === existingTab.id })),
              activeTabId: existingTab.id,
            },
          },
        };
      }

      const newStoreKey = item.type === 'conversation' ? crypto.randomUUID() : undefined;
      returnStoreKey = newStoreKey;

      const newTab: Tab = {
        id: tabId,
        itemId: item.id,
        itemType: item.type,
        title: item.name + (extensionIsNotable(item.filename, item.file_type) ? fileExtension(item.filename!) : ''),
        isActive: true,
        storeKey: newStoreKey,
      };

      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: {
            tabs: [...withRoom(projectState.tabs).map((t) => ({ ...t, isActive: false })), newTab],
            activeTabId: tabId,
          },
        },
      };
    });

    return returnStoreKey;
  },

  closeTab: (projectId: string, tabId: string) => {
    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      const closingTab = projectState.tabs.find((t) => t.id === tabId);
      const closingIndex = projectState.tabs.findIndex((t) => t.id === tabId);
      const filtered = projectState.tabs.filter((t) => t.id !== tabId);

      // Clean up stream stores
      if (closingTab?.itemType === 'conversation') {
        const storeKey = closingTab.storeKey || closingTab.itemId;
        if (storeKey) useConversationStreamStore.getState().cleanupConversation(storeKey);
      }

      if (filtered.length === 0) {
        return {
          tabsByProject: {
            ...state.tabsByProject,
            [projectId]: { tabs: [], activeTabId: null },
          },
        };
      }

      let newActiveTabId = projectState.activeTabId;
      let newTabs = filtered;

      if (tabId === projectState.activeTabId) {
        const newActiveIndex = Math.min(closingIndex, filtered.length - 1);
        const newActiveTab = filtered[newActiveIndex];
        newActiveTabId = newActiveTab.id;
        newTabs = filtered.map((t, i) => ({ ...t, isActive: i === newActiveIndex }));
      }

      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: { tabs: newTabs, activeTabId: newActiveTabId },
        },
      };
    });
  },

  setActiveTab: (projectId: string, tabId: string) => {
    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: {
            tabs: projectState.tabs.map((t) => ({ ...t, isActive: t.id === tabId })),
            activeTabId: tabId,
          },
        },
      };
    });
  },

  clearActiveTab: (projectId: string) => {
    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: {
            tabs: projectState.tabs.map((t) => ({ ...t, isActive: false })),
            activeTabId: null,
          },
        },
      };
    });
  },

  updateTabTitle: (projectId: string, itemId: string, newTitle: string) => {
    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      // Nothing to write: a set() here would still hand out a new
      // `tabsByProject`, and any caller driven by an effect would re-render,
      // re-fire and never settle.
      if (!projectState.tabs.some((t) => t.itemId === itemId && t.title !== newTitle)) {
        return state;
      }
      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: {
            ...projectState,
            tabs: projectState.tabs.map((t) => (t.itemId === itemId ? { ...t, title: newTitle } : t)),
          },
        },
      };
    });
  },

  openDraftTab: (projectId: string, title: string = 'New Conversation') => {
    const draftTabId = 'tab-draft-new';
    const storeKey = crypto.randomUUID();

    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      const existingDraft = projectState.tabs.find((t) => t.isDraft);

      if (existingDraft) {
        // Clean up the old storeKey's stream state before reusing the draft tab
        if (existingDraft.storeKey) {
          useConversationStreamStore.getState().cleanupConversation(existingDraft.storeKey);
        }
        return {
          tabsByProject: {
            ...state.tabsByProject,
            [projectId]: {
              tabs: projectState.tabs.map((t) =>
                t.isDraft ? { ...t, isActive: true, storeKey } : { ...t, isActive: false }
              ),
              activeTabId: draftTabId,
            },
          },
        };
      }

      const newTab: Tab = {
        id: draftTabId,
        itemId: 'draft-new',
        itemType: 'conversation',
        title,
        isActive: true,
        isDraft: true,
        storeKey,
      };

      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: {
            tabs: [...projectState.tabs.map((t) => ({ ...t, isActive: false })), newTab],
            activeTabId: draftTabId,
          },
        },
      };
    });

    return storeKey;
  },

  closeDraftTab: (projectId: string) => {
    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      const draftTab = projectState.tabs.find((t) => t.isDraft);

      if (draftTab?.storeKey) {
        useConversationStreamStore.getState().cleanupConversation(draftTab.storeKey);
      }

      const filtered = projectState.tabs.filter((t) => !t.isDraft);

      if (filtered.length === 0) {
        return {
          tabsByProject: {
            ...state.tabsByProject,
            [projectId]: { tabs: [], activeTabId: null },
          },
        };
      }

      const lastTab = filtered[filtered.length - 1];
      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: {
            tabs: filtered.map((t, i) => ({ ...t, isActive: i === filtered.length - 1 })),
            activeTabId: lastTab.id,
          },
        },
      };
    });
  },

  convertDraftToTab: (projectId: string, itemId: string, title?: string) => {
    const newTabId = `tab-${itemId}`;

    set((state) => {
      const projectState = state.tabsByProject[projectId] || emptyProjectState;
      const draftTab = projectState.tabs.find((t) => t.isDraft);
      const existingTab = projectState.tabs.find((t) => t.itemId === itemId && !t.isDraft);

      if (existingTab) {
        if (draftTab?.storeKey) {
          useConversationStreamStore.getState().cleanupConversation(draftTab.storeKey);
        }
        return {
          tabsByProject: {
            ...state.tabsByProject,
            [projectId]: {
              ...projectState,
              tabs: projectState.tabs.filter((t) => !t.isDraft),
            },
          },
        };
      }

      return {
        tabsByProject: {
          ...state.tabsByProject,
          [projectId]: {
            tabs: projectState.tabs.map((t) =>
              t.isDraft ? { ...t, id: newTabId, itemId, title: title || t.title, isDraft: false } : t
            ),
            activeTabId: newTabId,
          },
        },
      };
    });
  },

  rekey: (from, to) => {
    set((state) => {
      const moving = state.tabsByProject[from];
      if (!moving || from === to) return state;
      const target = state.tabsByProject[to] || emptyProjectState;
      const held = new Set(target.tabs.map((t) => t.itemId));
      const tabs = [...target.tabs, ...moving.tabs.filter((t) => !held.has(t.itemId))].slice(0, MAX_OPEN);
      const { [from]: _moved, ...rest } = state.tabsByProject;
      return {
        tabsByProject: { ...rest, [to]: { tabs, activeTabId: target.activeTabId ?? moving.activeTabId } },
        // A move is a change to keep, even right after a load.
        _loadedFromPreferences: false,
      };
    });
  },

  loadFromPreferences: (tabsByProject) => {
    if (!tabsByProject) return;

    const converted: Record<string, ProjectTabState> = {};
    for (const [projectId, state] of Object.entries(tabsByProject)) {
      converted[projectId] = {
        tabs: (state.tabs || []) as Tab[],
        activeTabId: state.activeTabId || null,
      };
    }

    set({ tabsByProject: converted, _loadedFromPreferences: true });
  },
}));

/**
 * Hook to get tabs for a specific project with bound actions.
 */
export function useProjectTabs(projectId: string | undefined) {
  const projectState = useTabStore((state) =>
    projectId ? state.tabsByProject[projectId] || emptyProjectState : emptyProjectState
  );

  const store = useTabStore.getState();

  return {
    tabs: projectState.tabs,
    activeTabId: projectState.activeTabId,
    activeTab: projectState.tabs.find((t) => t.id === projectState.activeTabId) || null,

    // Bound actions (no need to pass projectId)
    openTab: (item: TabItem) => (projectId ? store.openTab(projectId, item) : undefined),
    closeTab: (tabId: string) => projectId && store.closeTab(projectId, tabId),
    setActiveTab: (tabId: string) => projectId && store.setActiveTab(projectId, tabId),
    clearActiveTab: () => projectId && store.clearActiveTab(projectId),
    updateTabTitle: (itemId: string, title: string) => projectId && store.updateTabTitle(projectId, itemId, title),
    openDraftTab: (title?: string) => (projectId ? store.openDraftTab(projectId, title) : ''),
    closeDraftTab: () => projectId && store.closeDraftTab(projectId),
    convertDraftToTab: (itemId: string, title?: string) => projectId && store.convertDraftToTab(projectId, itemId, title),
  };
}
